import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getFunnelStatsDetails, getFunnelStatsExecutions } from '@/services/api/funnel';

export type FunnelPeriod = 'today' | '7d' | '30d';

export const PERIODS: { id: FunnelPeriod; label: string }[] = [
    { id: 'today', label: 'Hoje' },
    { id: '7d', label: '7 dias' },
    { id: '30d', label: '30 dias' },
];

const DAYS: Record<FunnelPeriod, number> = { today: 0, '7d': 6, '30d': 29 };

/** Limite do backend (/stats/*): até 93 dias por consulta. */
export const MAX_RANGE_DAYS = 93;

export interface DateRange {
    start: string;
    end: string;
}

export const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function rangeOf(period: FunnelPeriod): DateRange {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - DAYS[period]);
    return { start: ymd(start), end: ymd(end) };
}

/**
 * Detalhes + páginas do funil no período, para o painel de desempenho do builder.
 * `pageId` filtra só os detalhes: a lista de páginas vem sempre completa, para dar para trocar de página.
 */
export function useFunnelStats(funnelId: number | null, range: DateRange, enabled: boolean, pageId: string | null = null) {
    const { start, end } = range;
    const on = enabled && funnelId !== null;

    const details = useQuery({
        queryKey: ['funnel-stats-details', funnelId, start, end, pageId],
        queryFn: () => getFunnelStatsDetails(funnelId!, start, end, pageId),
        enabled: on,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
    });

    const executions = useQuery({
        queryKey: ['funnel-stats-executions', funnelId, start, end],
        queryFn: () => getFunnelStatsExecutions(funnelId!, start, end),
        enabled: on,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
    });

    return {
        details: details.data ?? null,
        pages: executions.data?.items ?? null,
        loading: details.isLoading || details.isPlaceholderData,
        error: details.isError,
        start,
        end,
    };
}
